import { Text, Box } from 'braid-design-system';
import React, { Fragment, type FunctionComponent } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import {
  PROGRESS_BAR_CHECKOUT,
  PROGRESS_BAR_CHECKOUT_LINK,
  PROGRESS_BAR_CLASSIFY,
  PROGRESS_BAR_CLASSIFY_LINK,
  PROGRESS_BAR_SELECT_AD_TYPE,
  PROGRESS_BAR_SELECT_AD_TYPE_LINK,
  PROGRESS_BAR_SHORTLIST,
  PROGRESS_BAR_SHORTLIST_LINK,
  PROGRESS_BAR_WRITE,
  PROGRESS_BAR_WRITE_LINK,
} from 'src/app/analytics/progressBar';
import {
  PAGE_ID_CLASSIFY,
  PAGE_ID_MANAGE,
  PAGE_ID_PAY_AND_POST,
  PAGE_ID_SELECT_AD_TYPE,
  PAGE_ID_WRITE,
} from 'src/app/constants/pageUid';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { NAVIGATION_BAR_EVENT } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateProgress } from 'src/app/state/modules/app/progress';
import { actionUpdateDraftProgress } from 'src/app/state/modules/formData/draftProgress';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { log } from 'src/app/utils/logging';
import parseValidationResult from 'src/app/utils/parseValidationResult';
import { getLastValidPage } from 'src/app/views/hooks/useLocalisedRoutes/utils/getLastValidPage';
import type AppState from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';
import type { TealiumEventData, TrackingEvent } from 'src/types/trackingData';

import { isPreviousStep, isLaterStep } from '../../utils';
import { Icon } from '../Icon';
import { Line } from '../Line';
import { STEP_STATE, ICON_STATE, LINE_STATE } from '../constants';

import * as styles from './index.css';

interface Props {
  handleDispatchDraft: () => DispatchDraftResponse;
  handleProgrammaticRouteChange: (stepId: string, isValid?: boolean) => void;
  handleValidation?: (skipIfNull?: boolean) => string[];
  stepPageId: string;
  text: string;
}

export const Step: FunctionComponent<Props> = ({
  handleDispatchDraft,
  handleProgrammaticRouteChange,
  handleValidation,
  stepPageId,
  text,
}) => {
  const { formData, progress, tealium } = useSelector(
    (state: AppState) => ({
      formData: state.formData,
      progress: state.app.progress,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const getState = () => {
    const { currentPageId, furthestPageId } = progress;
    const isValid = progress.isValidObj[stepPageId];
    let state;

    if (stepPageId === currentPageId) {
      state = STEP_STATE.CURRENT;
    } else if (isPreviousStep(stepPageId, currentPageId)) {
      state = STEP_STATE.PREVIOUS;
    } else if (isLaterStep(stepPageId, furthestPageId)) {
      state = STEP_STATE.LATER_NEW;
    } else if (isLaterStep(stepPageId, currentPageId)) {
      switch (isValid) {
        case undefined:
        case null:
          state = STEP_STATE.LATER_NO_VALIDATION;
          break;
        case false:
          state = STEP_STATE.LATER_INVALID;
          break;
        case true:
          state = STEP_STATE.LATER_VALID;
          break;
        default:
          log.error('ProgressBar Step | getState | Unexpected isValid', {
            isValid,
            currentPageId,
            furthestPageId,
          });
      }
    }

    return state;
  };

  const getIconState = (state: string) => {
    let iconState = '';

    switch (state) {
      case STEP_STATE.CURRENT:
        iconState = ICON_STATE.BLUE_DOT;
        break;
      case STEP_STATE.PREVIOUS:
        iconState = ICON_STATE.BLUE_TICK;
        break;
      case STEP_STATE.LATER_NEW:
        iconState = ICON_STATE.GRAY_CIRCLE;
        break;
      case STEP_STATE.LATER_NO_VALIDATION:
        iconState = ICON_STATE.GRAY_CIRCLE_FILLED;
        break;
      case STEP_STATE.LATER_INVALID:
        iconState = ICON_STATE.GRAY_CIRCLE_FILLED;
        break;
      case STEP_STATE.LATER_VALID:
        iconState = ICON_STATE.GRAY_TICK;
        break;
      default:
        throw new Error(`Step getIconState - unexpected state: ${state}`);
    }

    return iconState;
  };

  const getLineState = (state: string) => {
    let lineState = '';

    switch (state) {
      case STEP_STATE.CURRENT:
        lineState = LINE_STATE.BLUE_LINE;
        break;
      case STEP_STATE.PREVIOUS:
        lineState = LINE_STATE.BLUE_LINE;
        break;
      case STEP_STATE.LATER_NEW:
        lineState = LINE_STATE.GRAY_DOTTED_LINE;
        break;
      case STEP_STATE.LATER_NO_VALIDATION:
        lineState = LINE_STATE.GRAY_LINE;
        break;
      case STEP_STATE.LATER_INVALID:
        lineState = LINE_STATE.GRAY_LINE;
        break;
      case STEP_STATE.LATER_VALID:
        lineState = LINE_STATE.GRAY_LINE;
        break;
      default:
        throw new Error(`Step getIconState - unexpected state: ${state}`);
    }

    return lineState;
  };

  const getEventData = (currentPageId: string) => {
    let eventData = {};

    switch (currentPageId) {
      case PAGE_ID_CLASSIFY:
        eventData = {
          linkName: PROGRESS_BAR_CLASSIFY_LINK,
          eventName: [EVENT_NAME_INTERACTION, PROGRESS_BAR_CLASSIFY_LINK],
          objectInteraction: PROGRESS_BAR_CLASSIFY,
        };
        break;
      case PAGE_ID_SELECT_AD_TYPE:
        eventData = {
          linkName: PROGRESS_BAR_SELECT_AD_TYPE_LINK,
          eventName: [EVENT_NAME_INTERACTION, PROGRESS_BAR_SELECT_AD_TYPE_LINK],
          objectInteraction: PROGRESS_BAR_SELECT_AD_TYPE,
        };
        break;
      case PAGE_ID_WRITE:
        eventData = {
          linkName: PROGRESS_BAR_WRITE_LINK,
          eventName: [EVENT_NAME_INTERACTION, PROGRESS_BAR_WRITE_LINK],
          objectInteraction: PROGRESS_BAR_WRITE,
        };
        break;
      case PAGE_ID_MANAGE:
        eventData = {
          linkName: PROGRESS_BAR_SHORTLIST_LINK,
          eventName: [EVENT_NAME_INTERACTION, PROGRESS_BAR_SHORTLIST_LINK],
          objectInteraction: PROGRESS_BAR_SHORTLIST,
        };
        break;
      case PAGE_ID_PAY_AND_POST:
        eventData = {
          linkName: PROGRESS_BAR_CHECKOUT_LINK,
          eventName: [EVENT_NAME_INTERACTION, PROGRESS_BAR_CHECKOUT_LINK],
          objectInteraction: PROGRESS_BAR_CHECKOUT,
        };
        break;
      default:
        throw new Error(`unexpected pageId: ${currentPageId}`);
    }
    return eventData as TealiumEventData;
  };

  // Will be clickable if:
  // - step is not later than the furthest step visited
  // - step is any previous step
  // (note handleClick will restrict movement forward if validation fails on click)
  const isStepClickable = () => {
    const { currentPageId, furthestPageId } = progress;
    const furthestValidPageId = getLastValidPage(formData, furthestPageId);
    return (
      !isLaterStep(stepPageId, furthestValidPageId) ||
      isPreviousStep(stepPageId, currentPageId)
    );
  };

  const isPageValid = async (skipRequiredValidation = false) =>
    parseValidationResult(await handleValidation?.(skipRequiredValidation))
      .isValid;

  const handleClick = async () => {
    if (!isStepClickable()) {
      return;
    }

    const { currentPageId, furthestPageId } = progress;
    const isValid = await isPageValid();

    // Determine if navigation is allowed.
    // - Backwards navigation is allowed for mandatory fields that are empty. But not
    //   other types of validation errors such as exceeding max length (which cause save draft to fail)
    // - Forwards navigation is only allowed when the page is completely valid.
    if (isPreviousStep(stepPageId, currentPageId)) {
      // User is navigating backwards
      // validate again to see if page is partially valid (eg ignore mandatory fields)
      const isValidForBackNav = await isPageValid(true);

      if (isValidForBackNav) {
        // Tricky edge case: We need to prevent users from skipping over invalid steps.
        // This scenario can happen when a user:
        // 1. Completes a middle step (eg Write/Manage) and moves on (eg to PayAndPost), then comes back to it and makes it invalid
        // 2. Navigates further back (eg to Classify)
        // 3. Tries to jump over the invalid step to the later steps previously visited (eg tries to jump to PayAndPost).
        // To deal with this we clear previously visited steps that would allow this jump of invalid steps to occur.
        // So if the step is invalid and the user has been to steps after it, then reset the later steps to not visited.
        let resetFurthestStep = false;

        if (!isValid && isLaterStep(furthestPageId, currentPageId)) {
          resetFurthestStep = true;
        }
        // Everything is ok, now perform the navigation
        await performRouteChange(isValid, resetFurthestStep);
      }
    } else if (isValid) {
      // User is navigating forward. Only allow navigation when page is completely valid
      await performRouteChange(isValid);
    }
  };

  const performRouteChange = async (
    isValid: boolean,
    resetFurthestStep?: boolean,
  ) => {
    if (isPageAnalyticsEnabled) {
      const eventData: TrackingEvent = {
        eventName: NAVIGATION_BAR_EVENT,
        objectInteraction: `${'navigation-bar-'}${stepPageId}`,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      const eventData = getEventData(stepPageId);
      sendLinkEvent(tealium, eventData);
    }

    const newProgress = {
      currentPageId: progress.currentPageId,
      furthestPageId: resetFurthestStep
        ? progress.currentPageId
        : progress.furthestPageId,
      isValidObj: {
        ...progress.isValidObj,
        [progress.currentPageId]: isValid,
      },
    };

    await handleDispatchDraft?.();

    dispatch(actionUpdateProgress(newProgress));
    dispatch(actionUpdateDraftProgress(newProgress?.furthestPageId));

    handleProgrammaticRouteChange(stepPageId, isValid);
  };

  const state = getState() || '';
  const iconState = getIconState(state);
  const lineState = getLineState(state);
  const isClickable = isStepClickable();
  const isActiveStep = state === STEP_STATE.CURRENT;

  return (
    <Fragment>
      {stepPageId !== PAGE_ID_CLASSIFY && (
        <Box flexGrow={1} key={`${stepPageId}Line`}>
          <Line state={lineState} />
        </Box>
      )}
      <Box
        key={`${stepPageId}Icon`}
        position="relative"
        cursor={isClickable ? 'pointer' : 'default'}
        onClick={handleClick}
        data-testid={`${stepPageId}${iconState}`}
      >
        <Icon state={iconState} />
        <Box paddingTop="small" position="absolute" className={styles.label}>
          <Text weight={isActiveStep ? 'medium' : undefined}>{text}</Text>
        </Box>
      </Box>
    </Fragment>
  );
};
